package com.evoapps.evoshift.viewmodels

import com.evoapps.evoshift.domain.util.AppClock
import evoshift.composeapp.generated.resources.kiosk_photo_failed
import evoshift.composeapp.generated.resources.kiosk_qr_only_hint
import com.evoapps.evoshift.photo.ProfilePhotoProcessor
import com.evoapps.evoshift.domain.repository.ClockPhotoRepository
import com.evoapps.evoshift.domain.model.KioskSafeguards
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.model.ChecklistItem
import com.evoapps.evoshift.domain.model.ClockAction
import com.evoapps.evoshift.domain.model.KioskClockResult
import com.evoapps.evoshift.domain.model.KioskException
import com.evoapps.evoshift.domain.model.KioskPerson
import com.evoapps.evoshift.domain.model.KioskSession
import com.evoapps.evoshift.domain.model.KioskTile
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.repository.KioskChecklistMoment
import com.evoapps.evoshift.domain.repository.KioskChecklistRepository
import com.evoapps.evoshift.domain.repository.KioskRepository
import com.evoapps.evoshift.domain.usecase.kiosk.KioskBoard
import com.evoapps.evoshift.domain.usecase.kiosk.KioskClockQueue
import com.evoapps.evoshift.domain.usecase.kiosk.KioskClockUseCase
import com.evoapps.evoshift.kiosk.KioskDeviceLock
import com.evoapps.evoshift.kiosk.KioskLockHint
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toUiText
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.kiosk_checklist_offline
import evoshift.composeapp.generated.resources.kiosk_offline_waiting
import evoshift.composeapp.generated.resources.kiosk_sync_refused
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.flow.launchIn
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.onEach
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import kotlin.time.Clock
import kotlin.time.Instant

/** Where the tablet is: the grid, a search, a PIN pad, the confirmation, or leaving kiosk mode. */
sealed interface KioskStep {
    data object Grid : KioskStep

    data class Search(val query: String = "") : KioskStep

    /** The PIN pad for [person]; [tile] when they were picked from the grid (their shift). */
    data class Pin(val person: KioskPerson, val tile: KioskTile? = null, val digits: String = "") : KioskStep

    data class Done(val result: KioskClockResult) : KioskStep

    /** A manager leaves kiosk mode: picks themselves, then their PIN. */
    data class Exit(val manager: KioskPerson? = null, val digits: String = "") : KioskStep
}

/** SH-111: the rotating code beside the grid. */
sealed interface KioskQrState {
    /** Asking for the first code. */
    data object Loading : KioskQrState

    data class Showing(val token: String) : KioskQrState

    /** The code needs the server: offline, only the PIN works. */
    data object Offline : KioskQrState

    /** The workplace turned QR clocking off (or the server refuses it): no code. */
    data object Off : KioskQrState
}

data class KioskUiState(
    val workplaceName: String,
    val tiles: List<KioskTile> = emptyList(),
    val team: List<KioskPerson> = emptyList(),
    val step: KioskStep = KioskStep.Grid,
    /** Shown until the first shifts and team arrive. */
    val isLoading: Boolean = true,
    /** A clock request is on its way. */
    val isSending: Boolean = false,
    val error: UiText? = null,
    /** A note that stays until dismissed (offline events the server refused). */
    val notice: UiText? = null,
    /** Clock events waiting offline. */
    val pending: Int = 0,
    val lockHint: KioskLockHint = KioskLockHint.NONE,
    val qr: KioskQrState = KioskQrState.Loading,
    /** SH-134: the workplace's safeguards: QR only (no PIN pad), the optional clock-in photo. */
    val safeguards: KioskSafeguards = KioskSafeguards(),
    /** SH-134: after a PIN clock-in where the workplace takes photos: where the photo goes. */
    val photoPath: String? = null,
    /** SH-134: the photo is being processed and uploaded. */
    val photoSending: Boolean = false
) {
    /** "Someone else": the team without the people on the grid, filtered by the search. */
    val others: List<KioskPerson>
        get() = KioskBoard.others(team, tiles, (step as? KioskStep.Search)?.query.orEmpty())

    val managers: List<KioskPerson> get() = team.filter { it.isManager }
}

/**
 * SH-107: the clock-in tablet's screen. Follows the workplace's shifts of yesterday and today and
 * its team, rebuilds the grid every minute ([KioskBoard], the phone's clock rules), clocks with
 * [KioskClockUseCase] (offline into the queue, sent every [syncEveryMillis] once back online),
 * returns to the grid [DONE_MILLIS] after a confirmation and [IDLE_MILLIS] after the last touch,
 * and leaves kiosk mode only after a manager's PIN.
 */
@OptIn(ExperimentalCoroutinesApi::class)
class KioskViewModel(
    private val session: KioskSession,
    private val repository: KioskRepository,
    private val clock: KioskClockUseCase,
    private val queue: KioskClockQueue,
    private val deviceLock: KioskDeviceLock,
    private val now: () -> Instant = { AppClock.now() },
    private val ticks: () -> Flow<Unit> = { everyMillis(60_000) },
    private val syncTicks: () -> Flow<Unit> = { everyMillis(30_000) },
    /** Waits between QR fetches (SH-111); tests drive it by hand. */
    private val qrWait: suspend (Long) -> Unit = { delay(it) },
    /** SH-127: the shift's checklists around a clock-out / clock-in; null: none on this tablet. */
    checklists: KioskChecklistRepository? = null,
    /** SH-134: the optional clock-in photo (upload) and its processing (upright, small, no EXIF). */
    private val photos: ClockPhotoRepository? = null,
    private val photoProcessor: ProfilePhotoProcessor? = null
) : ViewModel() {

    val zone: TimeZone = session.timeZone?.let { runCatching { TimeZone.of(it) }.getOrNull() } ?: TimeZone.currentSystemDefault()

    private val _uiState = MutableStateFlow(KioskUiState(workplaceName = session.workplaceName, lockHint = deviceLock.hint))
    val uiState: StateFlow<KioskUiState> = _uiState.asStateFlow()

    private var resetJob: Job? = null

    /** SH-127: the checklists shown over the PIN pad (before a clock-out) or the confirmation. */
    private val checklistSession: KioskChecklistSession? = checklists?.let {
        KioskChecklistSession(it, viewModelScope, now, onActivity = { touched() }, onRefused = ::onFailure)
    }
    val checklist: StateFlow<KioskChecklistState?> = checklistSession?.state ?: MutableStateFlow(null)

    /** The "checklist needs a connection" note, cleared when the next person starts. */
    private var checklistNotice: UiText? = null

    private fun localNow(): LocalDateTime = now().toLocalDateTime(zone)

    init {
        deviceLock.enter()
        observeBoard()
        queue.events.onEach { events -> _uiState.update { it.copy(pending = events.size) } }.launchIn(viewModelScope)
        syncTicks().onEach { if (queue.events.value.isNotEmpty()) sync() }.launchIn(viewModelScope)
        rotateQr()
        repository.observeSafeguards(session.workplaceId)
            .onEach { safeguards -> _uiState.update { it.copy(safeguards = safeguards) } }
            .catch { }
            .launchIn(viewModelScope)
    }

    /**
     * SH-111: fetches a code, shows it, and fetches the next one 5 s before it expires. Offline the
     * code goes (the PIN keeps working) and is retried every [QR_RETRY_MILLIS]; turned off, the
     * switch is checked again every [QR_OFF_RECHECK_MILLIS]. An unpaired tablet signs out.
     */
    private fun rotateQr() {
        viewModelScope.launch {
            while (true) {
                val result = repository.qrCode()
                val error = result.exceptionOrNull()
                if (error is KioskException && error.reason == KioskException.Reason.REVOKED) {
                    onFailure(error)
                    return@launch
                }
                val code = result.getOrNull()
                val (qr, wait) = when {
                    error is KioskException -> KioskQrState.Off to QR_OFF_RECHECK_MILLIS
                    error != null -> KioskQrState.Offline to QR_RETRY_MILLIS
                    code == null -> KioskQrState.Off to QR_OFF_RECHECK_MILLIS
                    else -> KioskQrState.Showing(code.token) to code.refreshAfterMillis
                }
                _uiState.update { it.copy(qr = qr) }
                qrWait(wait)
            }
        }
    }

    private fun observeBoard() {
        // The days change at midnight: re-subscribe when they do.
        val days = ticks().map { KioskBoard.days(localNow()) }.distinctUntilChanged()
        val shifts: Flow<List<Shift>> = days.flatMapLatest { repository.observeShifts(session.workplaceId, it) }
        combine(shifts, repository.observeTeam(session.workplaceId), ticks()) { all, team, _ ->
            KioskBoard.tiles(all, team, localNow(), zone) to team
        }
            .onEach { (tiles, team) -> _uiState.update { it.copy(tiles = tiles, team = team, isLoading = false) } }
            .catch { e -> onFailure(e) }
            .launchIn(viewModelScope)
    }

    // --- Events --------------------------------------------------------------------------------

    fun choose(tile: KioskTile) = openPin(KioskStep.Pin(tile.person, tile))

    fun choose(person: KioskPerson) = openPin(KioskStep.Pin(person))

    private fun openPin(step: KioskStep.Pin) {
        clearChecklistNotice()
        // SH-134 "QR only": no PIN pad; the person scans the code with their own phone.
        if (_uiState.value.safeguards.qrOnly) {
            _uiState.update { it.copy(step = KioskStep.Grid, notice = UiText.Resource(Res.string.kiosk_qr_only_hint)) }
            return
        }
        _uiState.update { it.copy(step = step, error = null) }
        touched()
    }

    /**
     * SH-134: the photo taken after a PIN clock-in (the camera's file): turned upright, cropped to
     * a small square without metadata and uploaded to the path kioskClock gave. Never compared
     * with anything: it is only for a manager to look at.
     */
    fun photoTaken(bytes: ByteArray?) {
        val path = _uiState.value.photoPath ?: return
        if (bytes == null) return skipPhoto()
        _uiState.update { it.copy(photoSending = true) }
        viewModelScope.launch {
            val uploaded = runCatching {
                val processor = photoProcessor ?: error("no processor")
                val prepared = processor.prepare(bytes)
                val side = minOf(prepared.image.width, prepared.image.height)
                val crop = androidx.compose.ui.unit.IntRect(
                    (prepared.image.width - side) / 2, (prepared.image.height - side) / 2,
                    (prepared.image.width - side) / 2 + side, (prepared.image.height - side) / 2 + side
                )
                photos?.upload(path, processor.render(prepared, crop).jpeg)?.getOrThrow() ?: error("no storage")
            }.isSuccess
            _uiState.update {
                it.copy(
                    photoPath = null,
                    photoSending = false,
                    // Only a failure is worth a note on the tablet (it stays until dismissed).
                    notice = if (uploaded) it.notice else UiText.Resource(Res.string.kiosk_photo_failed)
                )
            }
            scheduleReset(DONE_MILLIS)
        }
    }

    /** SH-134: no photo this time (the clock-in stands). */
    fun skipPhoto() {
        _uiState.update { it.copy(photoPath = null, photoSending = false) }
        scheduleReset(DONE_MILLIS)
    }

    fun openSomeoneElse() {
        clearChecklistNotice()
        _uiState.update { it.copy(step = KioskStep.Search(), error = null) }
        touched()
    }

    fun search(query: String) {
        _uiState.update { s -> if (s.step is KioskStep.Search) s.copy(step = KioskStep.Search(query.take(40))) else s }
        touched()
    }

    fun openExit() {
        _uiState.update { it.copy(step = KioskStep.Exit(), error = null) }
        touched()
    }

    fun pickManager(manager: KioskPerson) {
        _uiState.update { s -> if (s.step is KioskStep.Exit) s.copy(step = KioskStep.Exit(manager), error = null) else s }
        touched()
    }

    fun digit(d: Char) {
        if (!d.isDigit()) return
        _uiState.update { s ->
            when (val step = s.step) {
                is KioskStep.Pin -> if (step.digits.length < PIN_LENGTH) s.copy(step = step.copy(digits = step.digits + d), error = null) else s
                is KioskStep.Exit -> if (step.manager != null && step.digits.length < PIN_LENGTH) s.copy(step = step.copy(digits = step.digits + d), error = null) else s
                else -> s
            }
        }
        touched()
    }

    fun backspace() {
        _uiState.update { s ->
            when (val step = s.step) {
                is KioskStep.Pin -> s.copy(step = step.copy(digits = step.digits.dropLast(1)))
                is KioskStep.Exit -> s.copy(step = step.copy(digits = step.digits.dropLast(1)))
                else -> s
            }
        }
        touched()
    }

    /** Back to the grid (cancel, or the inactivity timeout: a clock-out waiting for its checklist is not made). */
    fun backToGrid() {
        resetJob?.cancel()
        checklistSession?.clear()
        _uiState.update { it.copy(step = KioskStep.Grid, error = null, isSending = false, photoPath = null, photoSending = false) }
    }

    /** SH-127: the "checklist needs a connection" note goes when the next person starts. */
    private fun clearChecklistNotice() {
        val notice = checklistNotice ?: return
        checklistNotice = null
        _uiState.update { if (it.notice == notice) it.copy(notice = null) else it }
    }

    fun messageShown() = _uiState.update { it.copy(error = null) }

    fun noticeShown() = _uiState.update { it.copy(notice = null) }

    /** SH-116: the PIN pad's second button: start a break while working, or clock out during one. */
    fun submitOther() {
        val step = _uiState.value.step as? KioskStep.Pin ?: return
        step.tile?.otherAction?.let { submit(it) }
    }

    /**
     * The PIN pad's button: clock in or out, or end a break (the server decides from the shift);
     * [action] when the person chose it (SH-116: the second button).
     */
    fun submit(action: ClockAction? = null) {
        val step = _uiState.value.step as? KioskStep.Pin ?: return
        if (step.digits.length != PIN_LENGTH || _uiState.value.isSending) return
        _uiState.update { it.copy(isSending = true, error = null) }
        resetJob?.cancel()
        viewModelScope.launch {
            // SH-127: a clock-out first shows the shift's unfinished checklists; it is made when the
            // person ends the shift there (finished or skipped).
            val shiftId = step.tile?.shift?.id
            val session = checklistSession
            if (session != null && shiftId != null && (action ?: step.tile?.action) == ClockAction.OUT) {
                when (val opened = session.open(step.person, step.digits, shiftId, KioskChecklistMoment.CLOSE) { exit ->
                    clockNow(step, action, checklistOffline = !exit.skipSaved, checked = true)
                }) {
                    KioskChecklistSession.Opened.Shown -> {
                        _uiState.update { it.copy(isSending = false) }
                        touched()
                        return@launch
                    }
                    is KioskChecklistSession.Opened.Refused -> if (opened.error.stopsClock()) {
                        refused(opened.error)
                        return@launch
                    }
                    KioskChecklistSession.Opened.Offline -> {
                        clockNow(step, action, checklistOffline = true, checked = true)
                        return@launch
                    }
                    KioskChecklistSession.Opened.None -> {
                        clockNow(step, action, checked = true)
                        return@launch
                    }
                }
            }
            clockNow(step, action, checked = false)
        }
    }

    /**
     * Sends the clock event. [checked]: the closing checklists were looked at already;
     * [checklistOffline]: they could not be (the confirmation says so).
     */
    private suspend fun clockNow(step: KioskStep.Pin, action: ClockAction?, checklistOffline: Boolean = false, checked: Boolean) {
        _uiState.update { it.copy(isSending = true, error = null) }
        clock.clock(
            userId = step.person.userId,
            pin = step.digits,
            displayName = step.person.displayName,
            timeZone = zone,
            shiftId = step.tile?.shift?.id,
            expected = step.tile?.action,
            action = action
        ).fold(
            onSuccess = { result ->
                val notice = if (checklistOffline) UiText.Resource(Res.string.kiosk_checklist_offline) else null
                checklistNotice = notice
                // SH-134: where the workplace takes photos, the clock-in asks for one (or a skip).
                val photoPath = result.photoPath?.takeIf { photos != null && photoProcessor != null }
                _uiState.update { it.copy(step = KioskStep.Done(result), isSending = false, notice = notice ?: it.notice, photoPath = photoPath) }
                scheduleReset(if (photoPath != null) IDLE_MILLIS else DONE_MILLIS)
                if (!result.queued && queue.events.value.isNotEmpty()) sync()
                if (!result.queued) followUpChecklist(step, result, checked)
            },
            onFailure = { e -> refused(e) }
        )
    }

    /**
     * SH-127: after a clock-in, the opening checklists; after a clock-out that was not checked
     * first (the person came from "Someone else"), the unfinished ones. Over the confirmation.
     */
    private suspend fun followUpChecklist(step: KioskStep.Pin, result: KioskClockResult, checked: Boolean) {
        val session = checklistSession ?: return
        val shiftId = result.shiftId ?: step.tile?.shift?.id ?: return
        val moment = when (result.action) {
            ClockAction.IN -> KioskChecklistMoment.OPEN
            ClockAction.OUT -> if (checked) return else KioskChecklistMoment.CLOSE
            else -> return
        }
        val opened = session.open(step.person, step.digits, shiftId, moment) { backToGrid() }
        if (opened == KioskChecklistSession.Opened.Shown) touched()
    }

    /** Wrong PIN and the like: try again (the digits go, the person stays). */
    private fun refused(e: Throwable) {
        _uiState.update { s ->
            val pin = s.step as? KioskStep.Pin
            s.copy(isSending = false, error = e.toUiText(), step = pin?.copy(digits = "") ?: s.step)
        }
        touched()
        onFailure(e)
    }

    /** A checklist refusal that the clock would refuse alike: no point clocking. */
    private fun Throwable.stopsClock(): Boolean = this is KioskException && reason in CLOCK_REFUSALS

    // --- SH-127: the checklist on screen ---------------------------------------------------------

    fun toggleChecklistItem(checklistId: String, item: ChecklistItem) { checklistSession?.toggle(checklistId, item) }

    fun editChecklistNote(checklistId: String, item: ChecklistItem) { checklistSession?.editNote(checklistId, item) }

    fun updateChecklistNote(text: String) { checklistSession?.updateNote(text) }

    fun dismissChecklistNote() { checklistSession?.dismissNote() }

    fun saveChecklistNote() { checklistSession?.saveNote() }

    fun checklistMessageShown() { checklistSession?.messageShown() }

    /** Everything ticked: end the shift (closing) or back to the grid (opening). */
    fun finishChecklist() {
        if (checklist.value?.moment == KioskChecklistMoment.CLOSE) _uiState.update { it.copy(isSending = true) }
        checklistSession?.finish()
    }

    /** Go on without finishing; recorded, and the overdue reminder still goes out. */
    fun skipChecklist() {
        if (checklist.value?.moment == KioskChecklistMoment.CLOSE) _uiState.update { it.copy(isSending = true) }
        checklistSession?.skip()
    }

    /** Leaving kiosk mode: the manager's PIN, nothing left to send, then the tablet signs out. */
    fun submitExit() {
        val step = _uiState.value.step as? KioskStep.Exit ?: return
        val manager = step.manager ?: return
        if (step.digits.length != PIN_LENGTH || _uiState.value.isSending) return
        _uiState.update { it.copy(isSending = true, error = null) }
        viewModelScope.launch {
            repository.verifyExit(manager.userId, step.digits).fold(
                onSuccess = {
                    if (queue.events.value.isNotEmpty()) clock.sync()
                    val left = queue.events.value.size
                    if (left > 0) {
                        // PINs and times would be lost or left behind: send them first.
                        _uiState.update { it.copy(isSending = false, error = UiText.Resource(Res.string.kiosk_offline_waiting, left), step = step.copy(digits = "")) }
                        return@fold
                    }
                    leave()
                },
                onFailure = { e ->
                    _uiState.update { it.copy(isSending = false, error = e.toUiText(), step = step.copy(digits = "")) }
                    touched()
                    onFailure(e)
                }
            )
        }
    }

    private suspend fun sync() {
        val report = clock.sync()
        if (report.refused > 0) {
            _uiState.update { it.copy(notice = UiText.Resource(Res.string.kiosk_sync_refused, report.refused)) }
        }
    }

    /** An unpaired tablet signs out at once. */
    private fun onFailure(e: Throwable) {
        if (e is KioskException && e.reason == KioskException.Reason.REVOKED) {
            viewModelScope.launch {
                queue.clear()
                leave()
            }
        }
    }

    private suspend fun leave() {
        deviceLock.exit()
        repository.leave()
    }

    /** Any touch outside the grid restarts the inactivity timer (longer while a checklist is open). */
    private fun touched() = scheduleReset(if (checklist.value != null) CHECKLIST_IDLE_MILLIS else IDLE_MILLIS)

    private fun scheduleReset(millis: Long) {
        resetJob?.cancel()
        resetJob = viewModelScope.launch {
            delay(millis)
            backToGrid()
        }
    }

    companion object {
        /** PINs are exactly 6 digits. */
        const val PIN_LENGTH = 6
        const val DONE_MILLIS = 5_000L
        const val IDLE_MILLIS = 30_000L
        /** SH-127: a checklist takes walking around (the fridge, the till). */
        const val CHECKLIST_IDLE_MILLIS = 5 * 60_000L
        private val CLOCK_REFUSALS = setOf(
            KioskException.Reason.WRONG_PIN, KioskException.Reason.LOCKED, KioskException.Reason.NO_PIN,
            KioskException.Reason.PIN_SHAPE, KioskException.Reason.NOT_ON_TEAM, KioskException.Reason.REVOKED,
            KioskException.Reason.PLAN_ENDED
        )
        const val QR_RETRY_MILLIS = 10_000L
        const val QR_OFF_RECHECK_MILLIS = 60_000L
    }
}


private fun everyMillis(millis: Long): Flow<Unit> = flow {
    while (true) {
        emit(Unit)
        delay(millis)
    }
}
